<script lang="ts" setup>
import type { JobFilter } from '@/components/pages/job/JobFilter.vue'
import JobResult from '@/components/pages/job/JobResult.vue'
import SearchJob from '@/components/pages/job/SearchJob.vue'
import type { IJobResponse } from '@/types/job'

const { $api } = useNuxtApp()
const { t } = useI18n()
const router = useRouter()
const route = useRoute()

const monthYear = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric'
}).format(new Date())

const seoJobTerm = computed(() => route.query.search?.toString().trim() || null)

/**  Head configurations */
useSeoMeta({
    title: `Job Openings at ${monthYear}`,
    description: () => `Find ${seoJobTerm.value ? `${seoJobTerm.value} ` : ''}Jobs at Samurai Rekrutmen. Job Openings at ${monthYear}.`,
    keywords: () => `${seoJobTerm.value ? `${seoJobTerm.value}, ` : ''}all jobs, remote jobs, global vacancies, careers`
})

useHead({
    title: `Job Openings at ${monthYear}`
})

const isLoading = ref(true)
const isClearFilter = ref(false)
const isClearSearch = ref(false)

const hasActiveFilters = computed(() => {
    return Object.values(query.value.filter).some(
        (v) => v !== undefined && v !== null && v !== ''
    )
})
const jobs = ref<IJobResponse>({
    data: [],
    meta: { 
       current_page: 1, 
        last_page: 1, 
        total: 0, 
        per_page: 15,
        from: 1,
        to: 0,
        path: '',
        links: []
    }
})

const query = ref({
    filter: {
        search: route.query.search || undefined,
        province_id: Number(route.query.province_id) || undefined,
        category_id: Number(route.query.category_id) || undefined,
        job_type_id: Number(route.query.job_type_id) || undefined,
        jlpt_level: route.query.jlpt_level || undefined,
        is_jft_required:
            route.query.is_jft_required === 'true'
                ? true
                : route.query.is_jft_required === 'false'
                ? false
                : undefined,
        minimum_salary: Number(route.query.minimum_salary) || undefined,
        maximum_salary: Number(route.query.maximum_salary) || undefined,
        date_posted: route.query.date_posted || undefined
    },
    sort: 'relevance',
    page: 1
})

const sortItems = [
    { title: t('Relevance'), value: 'relevance' },
    { title: t('Highest Salary'), value: 'highest_salary' },
    { title: t('Most views'), value: 'most_views' },
    { title: t('Most applicants'), value: 'most_applicants' },
    { title: t('Newest'), value: 'newest' }
]

const searchJob = async (queryPayload?: any) => {
    try {
        isLoading.value = true
        const params = queryPayload || query.value
        const { data: jobsData } = await $api.job.getJobs({ query: params })
        if (jobsData.value) {
            // Logic: If first page, reset list. If next page, append.
            if (params.page === 1) {
                jobs.value.data = jobsData.value.data
            } else {
                // TypeScript now allows this because both are JobItem[]
                jobs.value.data = [...jobs.value.data, ...jobsData.value.data]
            }
            
            jobs.value.meta = jobsData.value.meta
        }
    } catch (err) {
        console.error('API Error:', err)
    } finally {
        isLoading.value = false
    }
}

const setFilter = (item:JobFilter) => {
    query.value.page = 1
    jobs.value.data = []
    query.value.filter = { ...query.value.filter, ...item }
}

const setSorting = () => {
    query.value.page = 1
    jobs.value.data = []
}

const loadMoreJob = () => {
    if (jobs.value.meta.current_page < jobs.value.meta.last_page) {
        query.value.page++
    }
}

const clearFilter = () => {
    query.value.page = 1
    jobs.value.data = []
    query.value.filter = {
        search: undefined,
        province_id: undefined,
        category_id: undefined,
        job_type_id: undefined,
        jlpt_level: undefined,
        is_jft_required: undefined,
        minimum_salary: undefined,
        maximum_salary: undefined,
        date_posted: undefined
    }

    isClearFilter.value = true
    isClearSearch.value = true
}

onMounted(async () => {
    await nextTick()
    await searchJob()
})

watch(
    query,
    (item) => {
        // Create a clean object for the URL
        const queryFilter: Record<string, string> = {}

        Object.entries(query.value.filter).forEach(([key, value]) => {
            // Only add to URL if the value is useful
            if (value !== null && value !== undefined && value !== '') {
                queryFilter[key] = String(value) // Convert numbers/booleans to string
            }
        })

        router.replace({
            name: 'job',
            query: queryFilter
        })

        searchJob(item)
    },
    { deep: true }
)
</script>

<template>
    <div class="job-page">
        <SearchJob v-model="isClearSearch" @set-filter="setFilter" />
        <div class="pt-2 pb-4 d-flex align-center justify-space-between">
            <h2 class="text-h5 mb-0">
                {{
                    route.query.search
                        ? ` ${t('Jobs related to')} "${route.query.search}"`
                        : t('Job Lists')
                }}
            </h2>
            <span v-if="hasActiveFilters" class="job-page__clear-filter" @click="clearFilter">
                <VIcon :size="20" icon="ri-close-line" />
                {{ t('Clear Filter') }}
            </span>
        </div>
        <!-- <pre>{{ jobs }}</pre> -->
        <JobFilter v-model="isClearFilter" @set-filter="setFilter" />
        <div
            class="d-flex align-center my-5 flex-wrap gap-4"
            :class="{
                'justify-end': isLoading,
                'justify-space-between': !isLoading
            }"
        >
            <div v-show="!isLoading">
                {{
                    t('jobs.showing', {
                        count: jobs.data.length,
                        total: jobs.meta.total
                    })
                }}
            </div>
            <div class="d-flex align-center gap-x-3">
                <span>{{ t('Sort by') }}</span>
                <VSelect
                    v-model="query.sort"
                    :placeholder="t('all_cities')"
                    style="inline-size: 220px"
                    :items="sortItems"
                    bg-color="surface"
                    density="compact"
                    @clear="query.sort = 'relevance'"
                    @update:model-value="setSorting"
                />
            </div>
        </div>

        <JobResult 
            v-if="!isLoading || jobs.data.length > 0" 
            :jobs="jobs.data" 
            :is-loading="isLoading" 
        />
        <template v-else>
            <VRow>
                 <VCol v-for="index in 9" :key="index" cols="12" md="6" lg="4">
                    <VSkeletonLoader type="image" />
                </VCol>
            </VRow>
        </template>
        <div
            v-if="
                !isLoading && jobs.meta.current_page < jobs.meta.last_page
            "
            class="text-center"
        >
            <VBtn class="mx-auto mt-10" @click="loadMoreJob">
                {{ t('See More Jobs') }}
            </VBtn>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.job-page {
    &__clear-filter {
        color: #ff1d25;

        &:hover {
            cursor: pointer;
        }
    }
}
</style>
